import type { AxiosError } from 'axios'
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  Factory,
  FileJson,
  FileSpreadsheet,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  Upload,
  XCircle
} from 'lucide-react'

import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { EmptyState } from '../components/ui/empty-state'
import { Input } from '../components/ui/input'
import { useToast } from '../components/ui/toast'
import { useAuth } from '../hooks/useAuth'
import {
  createOperator,
  deleteAllOperators,
  deleteOperator,
  getPlatformAdminTourOperators,
  getTourOperators,
  importTourOperators,
  ItineraryDay,
  PlatformAdminTourOperator,
  TourOperator,
  TourSummary,
  updateOperator,
  uploadOperatorLogo
} from '../lib/api/tourOperators'
import { HIKE_DIFFICULTY_LIST_TEXT, parseHikeDifficulty, parseHikeDifficultyMode, type HikeDifficulty, type HikeDifficultyMode } from '../lib/hikeDifficulty'
import {
  DEFAULT_PLAN_GENERATION_PROFILE,
  parseTourPlanGenerationProfile,
  toTourPlanGenerationProfile,
  type TourPlanGenerationProfile
} from '../lib/planGenerationProfile'
import { CSV_SCHEMA_TYPE_COLUMN, CSV_SCHEMA_VERSION_COLUMN, validateCsvSchemaFromMaps } from '../utils/csv'

const MAX_ITINERARY_DAYS = 16
const MILES_TO_KILOMETERS = 1.609344
const FEET_TO_METERS = 0.3048
const TOUR_OPERATORS_CSV_TYPE = 'TourOperators'
const TOUR_OPERATORS_CSV_VERSION = 2

const CSV_STATIC_COLUMNS = [
  CSV_SCHEMA_TYPE_COLUMN,
  CSV_SCHEMA_VERSION_COLUMN,
  'tour operator ID',
  'tour operator name',
  'trip name',
  'link to itinerary',
  'link to map',
  'UN geoscheme',
  'Country',
  'State or Province',
  'Geographic Region',
  'City',
  'Protected Area',
  'trip ID',
  'difficulty level',
  'difficulty mode',
  'plan generation profile',
  'pack weight',
  'highest point',
  'above 8k feet',
  'trip number of days',
  'multiple daily options',
  'total elevation gain meters',
  'total elevation loss meters',
  'total distance km'
] as const

const CSV_DAY_COLUMNS = Array.from({ length: MAX_ITINERARY_DAYS }, (_, index) => {
  const day = index + 1
  return [
    `day ${day} distance km`,
    `day ${day} elevation gain meters`,
    `day ${day} elevation loss meters`
  ]
}).flat()

const CSV_COLUMNS = [...CSV_STATIC_COLUMNS, ...CSV_DAY_COLUMNS]

type CsvValueMap = Record<string, string>

type RawCsvRecord = {
  rowNumber: number
  values: CsvValueMap
}

type ParsedCsvRow = {
  id: string
  rowNumber: number
  original: CsvValueMap
  normalized: CsvValueMap
}

type ImportStatus = {
  state: 'pending' | 'success' | 'error'
  message?: string
}

type OperatorLookups = {
  byId: Map<string, TourOperator>
  byName: Map<string, TourOperator>
  toursById: Map<string, { operator: TourOperator; tour: TourSummary }>
}

type DerivedImportRow = {
  id: string
  rowNumber: number
  operatorNameFromCsv: string
  operatorDisplayName: string
  operatorKey?: string
  tripName: string
  link?: string
  tripId?: string
  difficulty?: string
  difficultyLevel?: HikeDifficulty
  difficultyMode: HikeDifficultyMode
  planGenerationProfile?: TourPlanGenerationProfile
  packWeight?: string
  highestPoint?: string
  aboveEightThousandFeet?: boolean | null
  country?: string
  stateOrProvince?: string
  geographicRegion?: string
  city?: string
  protectedArea?: string
  itinerary: ItineraryDay[]
  errors: string[]
  matchedOperator?: TourOperator
  matchedTour?: TourSummary
  willCreateOperator: boolean
  action: 'update' | 'create' | 'create-with-operator' | 'blocked'
  sourceOperatorId?: string
}

type PreviewImportRow = DerivedImportRow & {
  status?: ImportStatus
}

const normalizeKey = (key: string) => key.trim().toLowerCase().replace(/\s+/g, ' ')

const normalizeName = (value: string) => value.trim().toLowerCase()

const isMongoObjectId = (value: string) => /^[a-fA-F0-9]{24}$/.test(value.trim())

const parseNumberCell = (value?: string): number | undefined => {
  if (!value) {
    return undefined
  }
  const trimmed = value.trim()
  if (!trimmed || trimmed.toLowerCase() === 'n/a') {
    return undefined
  }
  const sanitized = trimmed.replace(/,/g, '')
  const numericCandidate = sanitized.replace(/[^0-9.-]/g, '')
  if (!numericCandidate) {
    return undefined
  }
  const parsed = Number(numericCandidate)
  return Number.isFinite(parsed) ? parsed : undefined
}

const roundTo = (value: number, decimals: number) => {
  const factor = 10 ** decimals
  return Math.round(value * factor) / factor
}

const convertMilesToKilometers = (value: number) => roundTo(value * MILES_TO_KILOMETERS, 6)

const convertFeetToMeters = (value: number) => roundTo(value * FEET_TO_METERS, 6)

const parseSortPriority = (value: string) => {
  const parsed = Number.parseInt(value, 10)
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0
}

const parseBooleanCell = (value?: string): boolean | null => {
  if (!value?.trim()) {
    return null
  }
  return ['1', 'true', 'yes', 'y', 'on'].includes(value.trim().toLowerCase())
}

const formatBooleanCsv = (value?: boolean | null) => {
  if (value === true) return 'TRUE'
  if (value === false) return 'FALSE'
  return ''
}

const getFirstValue = (map: CsvValueMap, keys: string[]) => {
  for (const key of keys) {
    const candidate = map[key]
    if (candidate) {
      return candidate
    }
  }
  return ''
}

const buildItinerary = (map: CsvValueMap): ItineraryDay[] => {
  const days: ItineraryDay[] = []
  for (let day = 1; day <= MAX_ITINERARY_DAYS; day += 1) {
    const distanceKm = parseNumberCell(getFirstValue(map, [
      `day ${day} distance km`,
      `day ${day} distance kilometers`
    ]))
    const gainMeters = parseNumberCell(getFirstValue(map, [
      `day ${day} elevation gain meters`,
      `day ${day} elevation gain m`
    ]))
    const lossMeters = parseNumberCell(getFirstValue(map, [
      `day ${day} elevation loss meters`,
      `day ${day} elevation loss m`
    ]))
    const distanceMiles = parseNumberCell(getFirstValue(map, [
      `day ${day} distance miles`,
      `day ${day} distance mi`,
      `day ${day} distance`
    ]))
    const gainFeet = parseNumberCell(getFirstValue(map, [
      `day ${day} elevation gain feet`,
      `day ${day} elevation gain feet feet`,
      `day ${day} elevation gain ft`,
      `day ${day} elevation gain`
    ]))
    const lossFeet = parseNumberCell(getFirstValue(map, [
      `day ${day} elevation loss feet`,
      `day ${day} elevation loss feet feet`,
      `day ${day} elevation loss ft`,
      `day ${day} elevation loss`
    ]))

    if (distanceKm === undefined && gainMeters === undefined && lossMeters === undefined &&
        distanceMiles === undefined && gainFeet === undefined && lossFeet === undefined) {
      continue
    }

    days.push({
      dayNumber: day,
      distanceKm: distanceKm ?? (distanceMiles === undefined ? undefined : convertMilesToKilometers(distanceMiles)),
      elevationGainMeters: gainMeters ?? (gainFeet === undefined ? undefined : convertFeetToMeters(gainFeet)),
      elevationLossMeters: lossMeters ?? (lossFeet === undefined ? undefined : convertFeetToMeters(lossFeet))
    })
  }
  return days
}

const tokenizeCsv = (text: string) => {
  const rows: string[][] = []
  let current = ''
  let row: string[] = []
  let inQuotes = false

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index]
    if (char === '"') {
      if (inQuotes && text[index + 1] === '"') {
        current += '"'
        index += 1
      } else {
        inQuotes = !inQuotes
      }
      continue
    }

    if (char === ',' && !inQuotes) {
      row.push(current)
      current = ''
      continue
    }

    if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && text[index + 1] === '\n') {
        index += 1
      }
      row.push(current)
      rows.push(row)
      row = []
      current = ''
      continue
    }

    current += char
  }

  if (inQuotes) {
    throw new Error('CSV ended while inside a quoted field.')
  }

  if (current.length > 0 || row.length > 0) {
    row.push(current)
    rows.push(row)
  }

  return rows
}

const parseCsvText = (text: string): RawCsvRecord[] => {
  const rows = tokenizeCsv(text)
  if (rows.length === 0) {
    throw new Error('CSV file is empty.')
  }

  const headerRowIndex = rows.findIndex((row) => {
    const normalizedHeaders = row.map((cell) => normalizeKey(cell.replace(/^\ufeff/, '')))
    return normalizedHeaders.includes('tour operator name') && normalizedHeaders.includes('trip name')
  })

  if (headerRowIndex < 0) {
    throw new Error('CSV is missing the expected tour operator header row.')
  }

  const headerRow = rows[headerRowIndex]
  if (headerRow.length === 0) {
    throw new Error('CSV is missing column headers.')
  }

  headerRow[0] = headerRow[0].replace(/^\ufeff/, '')
  const headers = headerRow.map((header) => header.trim())
  const records: RawCsvRecord[] = []

  rows.slice(headerRowIndex + 1).forEach((values, index) => {
    const record: CsvValueMap = {}
    let hasContent = false

    headers.forEach((header, headerIndex) => {
      const cell = (values[headerIndex] ?? '').trim()
      record[header] = cell
      if (cell) {
        hasContent = true
      }
    })

    if (!hasContent) {
      return
    }

    records.push({
      rowNumber: headerRowIndex + index + 2,
      values: record
    })
  })

  return records
}

const buildParsedRows = (records: RawCsvRecord[]): ParsedCsvRow[] =>
  records.map((record, index) => {
    const normalized: CsvValueMap = {}
    Object.entries(record.values).forEach(([key, value]) => {
      normalized[normalizeKey(key)] = value.trim()
    })

    return {
      id: `csv-${record.rowNumber}-${index}-${Math.random().toString(36).slice(2, 8)}`,
      rowNumber: record.rowNumber,
      original: record.values,
      normalized
    }
  })

const buildOperatorLookups = (operators: TourOperator[]): OperatorLookups => {
  const byId = new Map<string, TourOperator>()
  const byName = new Map<string, TourOperator>()
  const toursById = new Map<string, { operator: TourOperator; tour: TourSummary }>()

  operators.forEach((operator) => {
    byId.set(operator.id, operator)
    const normalizedName = operator.name.trim().toLowerCase()
    if (normalizedName) {
      byName.set(normalizedName, operator)
    }
    operator.tours.forEach((tour) => {
      toursById.set(tour.id, { operator, tour })
    })
  })

  return { byId, byName, toursById }
}

const createPreviewRow = (row: ParsedCsvRow, lookups: OperatorLookups): DerivedImportRow => {
  const operatorId = getFirstValue(row.normalized, ['tour operator id', 'operator id'])
  const operatorNameRaw = getFirstValue(row.normalized, ['tour operator name', 'operator name'])
  const tripNameRaw = getFirstValue(row.normalized, ['trip name', 'tour name', 'name'])
  const linkRaw = getFirstValue(row.normalized, ['link to itinerary', 'link to map', 'url'])
  const tripId = getFirstValue(row.normalized, ['trip id', 'tour id', 'id'])
  const difficulty = getFirstValue(row.normalized, ['difficulty']).trim() || undefined
  const difficultyLevelRaw = getFirstValue(row.normalized, ['difficulty level', 'difficulty enum', 'new difficulty']).trim()
  const difficultyLevel = parseHikeDifficulty(difficultyLevelRaw) ?? undefined
  const difficultyModeRaw = getFirstValue(row.normalized, ['difficulty mode', 'rating mode']).trim()
  const difficultyMode = parseHikeDifficultyMode(difficultyModeRaw) ?? 'Calculated'
  const planGenerationProfileRaw = getFirstValue(row.normalized, ['plan generation profile', 'training plan profile', 'plan profile']).trim()
  const planGenerationProfile = parseTourPlanGenerationProfile(planGenerationProfileRaw) ?? undefined
  const packWeight = getFirstValue(row.normalized, ['pack weight', 'packweight']).trim() || undefined
  const highestPoint = getFirstValue(row.normalized, ['highest point', 'highestpoint']).trim() || undefined
  const aboveEightThousandFeet = parseBooleanCell(getFirstValue(row.normalized, ['above 8k feet', 'above 8k ft', 'above eight thousand feet']))
  const country = getFirstValue(row.normalized, ['country']).trim() || undefined
  const stateOrProvince = getFirstValue(row.normalized, ['state or province', 'state', 'province']).trim() || undefined
  const geographicRegion = getFirstValue(row.normalized, ['geographic region', 'region']).trim() || undefined
  const city = getFirstValue(row.normalized, ['city']).trim() || undefined
  const protectedArea = getFirstValue(row.normalized, ['protected area', 'park', 'preserve']).trim() || undefined
  const itinerary = buildItinerary(row.normalized)

  const operatorName = operatorNameRaw.trim()
  const tripName = tripNameRaw.trim()
  const link = linkRaw.trim() || undefined

  let matchedOperator = tripId ? lookups.toursById.get(tripId)?.operator : undefined
  let matchedTour = tripId ? lookups.toursById.get(tripId)?.tour : undefined

  if (!matchedOperator && operatorId) {
    matchedOperator = lookups.byId.get(operatorId)
  }

  const operatorKey = operatorName ? normalizeName(operatorName) : undefined

  if (!matchedOperator && operatorKey) {
    matchedOperator = lookups.byName.get(operatorKey)
  }

  const tripNameKey = tripName ? normalizeName(tripName) : undefined
  if (!matchedTour && matchedOperator && tripNameKey) {
    matchedTour = matchedOperator.tours.find((tour) => normalizeName(tour.name) === tripNameKey)
  }

  const errors: string[] = []
  if (!matchedOperator && !operatorName) {
    errors.push('Missing tour operator name.')
  }
  if (!tripName) {
    errors.push('Missing trip name.')
  }
  if (difficultyLevelRaw && !difficultyLevel) {
    errors.push(`Difficulty level must be ${HIKE_DIFFICULTY_LIST_TEXT}.`)
  }
  if (difficultyModeRaw && !parseHikeDifficultyMode(difficultyModeRaw)) {
    errors.push('Difficulty mode must be Legacy, Calculated, or Manual.')
  }
  if (difficultyMode === 'Manual' && !difficultyLevel) {
    errors.push('Manual difficulty requires a difficulty level.')
  }
  if (planGenerationProfileRaw && !planGenerationProfile) {
    errors.push('Plan generation profile must be Standard or DayHike.')
  }

  const willCreateOperator = !matchedOperator && !!operatorName
  const action: DerivedImportRow['action'] = matchedTour
    ? 'update'
    : matchedOperator
      ? 'create'
      : willCreateOperator
        ? 'create-with-operator'
        : 'blocked'

  if (action === 'blocked' && errors.length === 0) {
    errors.push('Add an operator name so we can create it.')
  }

  return {
    id: row.id,
    rowNumber: row.rowNumber,
    operatorNameFromCsv: operatorName,
    operatorDisplayName: operatorName || matchedOperator?.name || '',
    operatorKey,
    tripName,
    link,
    tripId: tripId || undefined,
    difficulty,
    difficultyLevel,
    difficultyMode,
    planGenerationProfile,
    packWeight,
    highestPoint,
    aboveEightThousandFeet,
    country,
    stateOrProvince,
    geographicRegion,
    city,
    protectedArea,
    itinerary,
    errors,
    matchedOperator,
    matchedTour,
    willCreateOperator,
    action,
    sourceOperatorId: operatorId || undefined
  }
}

const formatActionLabel = (action: DerivedImportRow['action']) => {
  switch (action) {
    case 'update':
      return { label: 'Replace operator tours', className: 'bg-sky-50 text-sky-700' }
    case 'create':
      return { label: 'Replace operator tours', className: 'bg-emerald-50 text-emerald-700' }
    case 'create-with-operator':
      return { label: 'Create operator + replace tours', className: 'bg-purple-50 text-purple-700' }
    default:
      return { label: 'Blocked', className: 'bg-rose-50 text-rose-700' }
  }
}

const getImportGroupKey = (row: DerivedImportRow) => {
  const fallbackName = row.operatorNameFromCsv.trim() ? normalizeName(row.operatorNameFromCsv) : ''
  const groupKey = row.matchedOperator?.id ?? row.sourceOperatorId?.trim() ?? row.operatorKey ?? fallbackName
  return groupKey || row.id
}

const buildImportOperatorPayload = (operator: TourOperator, rows: PreviewImportRow[]): TourOperator => {
  const seenTourIds = new Set<string>()
  const importedOperatorName =
    rows.find((row) => row.operatorNameFromCsv.trim())?.operatorNameFromCsv.trim() ?? operator.name

  return {
    ...operator,
    name: importedOperatorName,
    tours: rows.map((row) => {
      const matchedTour = row.matchedTour?.operatorId === operator.id ? row.matchedTour : undefined
      let tourId = matchedTour?.id ?? (row.tripId && isMongoObjectId(row.tripId) ? row.tripId : '')
      if (tourId && seenTourIds.has(tourId)) {
        tourId = ''
      } else if (tourId) {
        seenTourIds.add(tourId)
      }

      return {
        id: tourId,
        operatorId: operator.id,
        name: row.tripName,
        link: row.link ?? null,
        country: row.country ?? null,
        stateOrProvince: row.stateOrProvince ?? null,
        geographicRegion: row.geographicRegion ?? null,
        city: row.city ?? null,
        protectedArea: row.protectedArea ?? null,
        difficulty: row.difficulty ?? matchedTour?.difficulty ?? null,
        difficultyLevel: row.difficultyMode === 'Calculated' ? null : row.difficultyLevel ?? null,
        difficultyMode: row.difficultyMode,
        planGenerationProfile: row.planGenerationProfile ?? toTourPlanGenerationProfile(matchedTour?.planGenerationProfile),
        packWeight: row.packWeight ?? null,
        highestPoint: row.highestPoint ?? null,
        aboveEightThousandFeet: row.aboveEightThousandFeet ?? null,
        itinerary: row.itinerary.map((day) => ({ ...day })),
        totalDistanceKm: null,
        totalElevationGainMeters: null,
        totalElevationLossMeters: null,
        heroImageUrls: tourId && matchedTour?.id === tourId ? [...matchedTour.heroImageUrls] : []
      }
    })
  }
}

const flattenValidationErrors = (errors: unknown): string | undefined => {
  if (!errors || typeof errors !== 'object') {
    return undefined
  }
  const entries: string[] = []
  Object.entries(errors as Record<string, unknown>).forEach(([field, value]) => {
    if (Array.isArray(value)) {
      value.forEach((message) => {
        if (typeof message === 'string' && message.trim()) {
          entries.push(`${field}: ${message}`)
        }
      })
    } else if (typeof value === 'string' && value.trim()) {
      entries.push(`${field}: ${value}`)
    }
  })
  return entries.length > 0 ? entries.join(' ') : undefined
}

const getErrorMessage = (error: unknown) => {
  const axiosError = error as AxiosError | undefined
  const responseData = axiosError?.response?.data
  if (axiosError?.isAxiosError) {
    const status = axiosError.response?.status
    if (status === 400 && responseData) {
      if (typeof responseData === 'string') {
        return responseData
      }
      if (typeof responseData === 'object') {
        const validationDetails =
          flattenValidationErrors((responseData as Record<string, unknown>).errors) ??
          flattenValidationErrors(responseData)
        if (validationDetails) {
          return `Validation failed: ${validationDetails}`
        }
        const message =
          (responseData as { message?: string }).message ??
          (responseData as { title?: string }).title
        if (message) {
          return message
        }
      }
    }
    if (responseData && typeof responseData === 'string') {
      return responseData
    }
  }
  if (error instanceof Error) {
    return error.message
  }
  return 'Unexpected error occurred.'
}

const formatNumberValue = (value?: number | null) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return ''
  }
  const normalized = Number(value)
  return Number.isFinite(normalized) ? `${normalized}` : ''
}

const escapeCsvValue = (value: string) => {
  if (value.includes('"') || value.includes(',') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

const buildCsvForOperators = (operators: TourOperator[]) => {
  const rows: string[] = []

  for (const operator of operators) {
    for (const tour of operator.tours) {
      const itinerary = [...tour.itinerary].sort((a, b) => a.dayNumber - b.dayNumber)
      const distanceProvided = itinerary.some((day) => day.distanceKm !== null && day.distanceKm !== undefined)
      const gainProvided = itinerary.some((day) => day.elevationGainMeters !== null && day.elevationGainMeters !== undefined)
      const lossProvided = itinerary.some((day) => day.elevationLossMeters !== null && day.elevationLossMeters !== undefined)

      const totalDistance = itinerary.reduce((sum, day) => sum + (day.distanceKm ?? 0), 0)
      const totalGain = itinerary.reduce((sum, day) => sum + (day.elevationGainMeters ?? 0), 0)
      const totalLoss = itinerary.reduce((sum, day) => sum + (day.elevationLossMeters ?? 0), 0)

      const map: Record<string, string> = {
        [CSV_SCHEMA_TYPE_COLUMN]: TOUR_OPERATORS_CSV_TYPE,
        [CSV_SCHEMA_VERSION_COLUMN]: TOUR_OPERATORS_CSV_VERSION.toString(),
        'tour operator ID': operator.id,
        'tour operator name': operator.name,
        'trip name': tour.name,
        'link to itinerary': tour.link ?? '',
        'link to map': '',
        'UN geoscheme': '',
        Country: tour.country ?? '',
        'State or Province': tour.stateOrProvince ?? '',
        'Geographic Region': tour.geographicRegion ?? '',
        City: tour.city ?? '',
        'Protected Area': tour.protectedArea ?? '',
        'trip ID': tour.id,
        'difficulty level': parseHikeDifficulty(tour.difficultyLevel) ?? '',
        'difficulty mode': tour.difficultyMode ?? 'Legacy',
        'plan generation profile': tour.planGenerationProfile ?? DEFAULT_PLAN_GENERATION_PROFILE,
        'pack weight': tour.packWeight ?? '',
        'highest point': tour.highestPoint ?? '',
        'above 8k feet': formatBooleanCsv(tour.aboveEightThousandFeet),
        'trip number of days': itinerary.length ? `${itinerary.length}` : '',
        'multiple daily options': '',
        'total elevation gain meters': gainProvided ? formatNumberValue(totalGain) : '',
        'total elevation loss meters': lossProvided ? formatNumberValue(totalLoss) : '',
        'total distance km': distanceProvided ? formatNumberValue(totalDistance) : ''
      }

      for (let index = 0; index < MAX_ITINERARY_DAYS; index += 1) {
        const dayNumber = index + 1
        const itineraryEntry = itinerary.find((day) => day.dayNumber === dayNumber)
        map[`day ${dayNumber} distance km`] = formatNumberValue(itineraryEntry?.distanceKm)
        map[`day ${dayNumber} elevation gain meters`] = formatNumberValue(itineraryEntry?.elevationGainMeters)
        map[`day ${dayNumber} elevation loss meters`] = formatNumberValue(itineraryEntry?.elevationLossMeters)
      }

      const rowValues = CSV_COLUMNS.map((column) => escapeCsvValue(map[column] ?? ''))
      rows.push(rowValues.join(','))
    }
  }

  return [CSV_COLUMNS.join(','), ...rows].join('\n')
}

const generateShortId = (name: string): string => {
  const words = name.split(/\s+/).filter(Boolean)
  if (words.length >= 2) {
    const w1 = words[0].toUpperCase().replace(/[^A-Z0-9]/g, '')
    const w2 = words[1].toUpperCase().replace(/[^A-Z0-9]/g, '')
    return (w1.slice(0, 3) + w2.slice(0, 2)).slice(0, 5)
  }
  const clean = name.toUpperCase().replace(/[^A-Z0-9]/g, '')
  return clean.slice(0, 5) || ''
}

export const TourOperatorsAdminPage = () => {
  const { user } = useAuth()
  const { addToast } = useToast()
  const isAdmin = user?.role === 'Admin' || user?.role === 'Editor'

  const [operators, setOperators] = useState<PlatformAdminTourOperator[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newShortId, setNewShortId] = useState('')
  const [newReferralCode, setNewReferralCode] = useState('')
  const [newSortPriority, setNewSortPriority] = useState('0')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [editShortId, setEditShortId] = useState('')
  const [editReferralCode, setEditReferralCode] = useState('')
  const [editSortPriority, setEditSortPriority] = useState('0')
  const [savingId, setSavingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deletingAll, setDeletingAll] = useState(false)
  const [parsedRows, setParsedRows] = useState<ParsedCsvRow[]>([])
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(() => new Set())
  const [rowStatuses, setRowStatuses] = useState<Record<string, ImportStatus | undefined>>({})
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null)
  const [importingRows, setImportingRows] = useState(false)
  const [exporting, setExporting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [importingJson, setImportingJson] = useState(false)
  const [exportingJson, setExportingJson] = useState(false)
  const jsonFileInputRef = useRef<HTMLInputElement | null>(null)
  const [uploadingLogoId, setUploadingLogoId] = useState<string | null>(null)

  const loadOperators = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getPlatformAdminTourOperators()
      setOperators(data)
    } catch (err) {
      console.error('Failed to load operators', err)
      setError('Unable to load operators right now.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isAdmin) {
      void loadOperators()
    }
  }, [isAdmin, loadOperators])

  const operatorLookups = useMemo(() => buildOperatorLookups(operators), [operators])

  const previewRows = useMemo<PreviewImportRow[]>(() => {
    return parsedRows.map((row) => {
      const derived = createPreviewRow(row, operatorLookups)
      return {
        ...derived,
        status: rowStatuses[row.id]
      }
    })
  }, [parsedRows, operatorLookups, rowStatuses])

  const selectableRows = useMemo(() => previewRows.filter((row) => row.errors.length === 0), [previewRows])

  const selectedCount = useMemo(
    () => selectableRows.filter((row) => selectedRowIds.has(row.id)).length,
    [selectableRows, selectedRowIds]
  )

  const selectedOperatorCount = useMemo(() => {
    return new Set(
      previewRows
        .filter((row) => selectedRowIds.has(row.id) && row.errors.length === 0)
        .map(getImportGroupKey)
    ).size
  }, [previewRows, selectedRowIds])

  const isAllSelected = selectableRows.length > 0 && selectedCount === selectableRows.length

  const sortedOperators = useMemo(() => {
    return [...operators].sort(
      (a, b) => b.sortPriority - a.sortPriority || a.name.localeCompare(b.name)
    )
  }, [operators])

  const handleCreateOperator = async () => {
    if (!newName.trim()) {
      addToast({ variant: 'error', title: 'Name required', description: 'Add a name before creating an operator.' })
      return
    }
    setCreating(true)
    try {
      const created = await createOperator(
        newName.trim(),
        newReferralCode.trim() || null,
        newShortId.trim() || null,
        parseSortPriority(newSortPriority)
      )
      setOperators((prev) => [...prev, created])
      setNewName('')
      setNewShortId('')
      setNewReferralCode('')
      setNewSortPriority('0')
      addToast({ variant: 'success', title: 'Operator added', description: `${created.name} created successfully.` })
    } catch (err) {
      console.error('Failed to create operator', err)
      addToast({ variant: 'error', title: 'Unable to create operator', description: 'Try again or check your permissions.' })
    } finally {
      setCreating(false)
    }
  }

  const startEditing = (operator: PlatformAdminTourOperator) => {
    setEditingId(operator.id)
    setEditName(operator.name)
    setEditShortId(operator.shortId ?? '')
    setEditReferralCode(operator.referralCode ?? '')
    setEditSortPriority(String(operator.sortPriority))
  }

  const handleSaveOperator = async () => {
    if (!editingId) {
      return
    }
    if (!editName.trim()) {
      addToast({ variant: 'error', title: 'Name required', description: 'Operator name cannot be empty.' })
      return
    }
    setSavingId(editingId)
    try {
      const updated = await updateOperator(
        editingId,
        editName.trim(),
        editReferralCode.trim() || null,
        editShortId.trim() || null,
        parseSortPriority(editSortPriority)
      )
      setOperators((prev) => prev.map((op) => (op.id === updated.id ? updated : op)))
      addToast({ variant: 'success', title: 'Operator updated', description: `${updated.name} saved successfully.` })
      setEditingId(null)
      setEditName('')
      setEditShortId('')
      setEditReferralCode('')
      setEditSortPriority('0')
    } catch (err) {
      console.error('Failed to update operator', err)
      addToast({ variant: 'error', title: 'Unable to update operator', description: 'Try again shortly.' })
    } finally {
      setSavingId(null)
    }
  }

  const handleDeleteOperator = async (operator: TourOperator) => {
    if (operator.tours.length > 0) {
      addToast({ variant: 'error', title: 'Operator has tours', description: 'Remove or reassign tours before deleting this operator.' })
      return
    }
    if (!window.confirm(`Delete operator ${operator.name}? This cannot be undone.`)) {
      return
    }
    setDeletingId(operator.id)
    try {
      await deleteOperator(operator.id)
      setOperators((prev) => prev.filter((op) => op.id !== operator.id))
      addToast({ variant: 'success', title: 'Operator deleted', description: `${operator.name} removed.` })
      if (editingId === operator.id) {
        setEditingId(null)
        setEditName('')
        setEditSortPriority('0')
      }
    } catch (err) {
      console.error('Failed to delete operator', err)
      addToast({ variant: 'error', title: 'Unable to delete operator', description: 'Try again later.' })
    } finally {
      setDeletingId(null)
    }
  }

  const handleDeleteAllOperators = useCallback(async () => {
    const confirmed = window.confirm(
      'WARNING: This will permanently delete ALL operators from the database. Their embedded tours will also be removed. This action cannot be undone.'
    )
    if (!confirmed) {
      return
    }

    const doubleConfirmed = window.confirm(
      'Final confirmation: delete every operator and all of their tours? Click OK to proceed.'
    )
    if (!doubleConfirmed) {
      return
    }

    setDeletingAll(true)
    try {
      const result = await deleteAllOperators()
      setOperators([])
      setEditingId(null)
      setEditName('')
      setEditShortId('')
      setEditReferralCode('')
      addToast({
        variant: 'success',
        title: 'Operators deleted',
        description:
          `Removed ${result.deletedOperatorCount} operator${result.deletedOperatorCount === 1 ? '' : 's'} ` +
          `and ${result.deletedTourCount} tour${result.deletedTourCount === 1 ? '' : 's'}.`
      })
    } catch (err) {
      console.error('Failed to delete operators in bulk', err)
      addToast({
        variant: 'error',
        title: 'Delete failed',
        description: 'Unable to delete all operators right now.'
      })
    } finally {
      setDeletingAll(false)
    }
  }, [addToast])

  const handleLogoUpload = async (operator: TourOperator, file: File) => {
    setUploadingLogoId(operator.id)
    try {
      const upload = await uploadOperatorLogo(operator.id, file)
      setOperators((prev) =>
        prev.map((op) => (op.id === operator.id ? { ...op, logoUrl: upload.logoUrl } : op))
      )
      addToast({ variant: 'success', title: 'Logo updated', description: `${operator.name} logo has been updated.` })
    } catch (err) {
      console.error('Failed to upload logo', err)
      addToast({ variant: 'error', title: 'Upload failed', description: 'Unable to upload logo. Please try again.' })
    } finally {
      setUploadingLogoId(null)
    }
  }

  const handleFileChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) {
        return
      }
      try {
        const parsedRecords = buildParsedRows(parseCsvText(await file.text()))
        if (parsedRecords.length === 0) {
          throw new Error('No rows with data were found in this CSV.')
        }
        const schemaResult = validateCsvSchemaFromMaps(parsedRecords.map((row) => row.normalized), {
          expectedType: TOUR_OPERATORS_CSV_TYPE,
          currentVersion: TOUR_OPERATORS_CSV_VERSION,
          entityName: 'Tour operators'
        })
        if (schemaResult.warning) {
          addToast({ title: 'Legacy CSV format', description: schemaResult.warning, duration: 9000 })
        }
        const validIds = parsedRecords
          .map((row) => {
            const derived = createPreviewRow(row, operatorLookups)
            return derived.errors.length === 0 ? row.id : null
          })
          .filter((rowId): rowId is string => Boolean(rowId))

        setParsedRows(parsedRecords)
        setRowStatuses({})
        setSelectedRowIds(new Set(validIds))
        setUploadedFileName(file.name)
        addToast({
          variant: 'success',
          title: 'CSV loaded',
          description: `Loaded ${parsedRecords.length} row${parsedRecords.length === 1 ? '' : 's'} from ${file.name}.`
        })
      } catch (error) {
        addToast({ variant: 'error', title: 'Unable to read CSV', description: getErrorMessage(error) })
      } finally {
        event.target.value = ''
      }
    },
    [operatorLookups, addToast]
  )

  const handleClearImport = () => {
    setParsedRows([])
    setSelectedRowIds(new Set())
    setRowStatuses({})
    setUploadedFileName(null)
  }

  const toggleSelectAll = () => {
    if (selectableRows.length === 0) {
      return
    }
    if (isAllSelected) {
      setSelectedRowIds(new Set())
    } else {
      setSelectedRowIds(new Set(selectableRows.map((row) => row.id)))
    }
  }

  const toggleRowSelection = (rowId: string, disabled?: boolean) => {
    if (disabled) {
      return
    }
    setSelectedRowIds((prev) => {
      const next = new Set(prev)
      if (next.has(rowId)) {
        next.delete(rowId)
      } else {
        next.add(rowId)
      }
      return next
    })
  }

  const resolveOperatorForRow = async (row: DerivedImportRow, cache: Map<string, TourOperator>) => {
    if (row.matchedOperator) {
      return row.matchedOperator
    }
    if (!row.operatorKey || !row.operatorNameFromCsv) {
      throw new Error('Operator name is required to create a new company.')
    }
    if (cache.has(row.operatorKey)) {
      return cache.get(row.operatorKey)!
    }
    const created = await createOperator(row.operatorNameFromCsv)
    cache.set(row.operatorKey, created)
    return created
  }

  const handleImportSelected = async () => {
    const rowsToImport = previewRows.filter((row) => selectedRowIds.has(row.id) && row.errors.length === 0)
    if (rowsToImport.length === 0) {
      addToast({
        variant: 'error',
        title: 'No valid rows selected',
        description: 'Select at least one row without errors to import.'
      })
      return
    }

    const operatorCount = new Set(rowsToImport.map(getImportGroupKey)).size
    const confirmed = window.confirm(
      `Replace all existing tours for ${operatorCount} operator${operatorCount === 1 ? '' : 's'} with the selected CSV rows? ` +
      `Only selected rows will remain for those operators. Explicit km/meters headers are imported as metric; legacy distance/elevation headers are converted from miles/feet.`
    )
    if (!confirmed) {
      return
    }

    setImportingRows(true)
    const operatorCache = new Map<string, TourOperator>()
    const groupedRows = new Map<string, { operator: TourOperator; rows: PreviewImportRow[] }>()
    setRowStatuses((prev) => {
      const next = { ...prev }
      rowsToImport.forEach((row) => {
        next[row.id] = { state: 'pending', message: 'Preparing replacement…' }
      })
      return next
    })

    let successCount = 0
    let failureCount = 0
    let successfulOperatorCount = 0

    for (const row of rowsToImport) {
      try {
        const operator = await resolveOperatorForRow(row, operatorCache)
        const existingGroup = groupedRows.get(operator.id)
        if (existingGroup) {
          existingGroup.rows.push(row)
        } else {
          groupedRows.set(operator.id, { operator, rows: [row] })
        }
      } catch (error) {
        failureCount += 1
        setRowStatuses((prev) => ({
          ...prev,
          [row.id]: {
            state: 'error',
            message: getErrorMessage(error)
          }
        }))
      }
    }

    for (const { operator, rows } of groupedRows.values()) {
      setRowStatuses((prev) => {
        const next = { ...prev }
        rows.forEach((row) => {
          next[row.id] = { state: 'pending', message: `Replacing tours for ${operator.name}…` }
        })
        return next
      })

      try {
        await importTourOperators([buildImportOperatorPayload(operator, rows)])
        successCount += rows.length
        successfulOperatorCount += 1
        setRowStatuses((prev) => {
          const next = { ...prev }
          rows.forEach((row) => {
            next[row.id] = {
              state: 'success',
              message: `${operator.name} synced`
            }
          })
          return next
        })
      } catch (error) {
        failureCount += rows.length
        const message = getErrorMessage(error)
        setRowStatuses((prev) => {
          const next = { ...prev }
          rows.forEach((row) => {
            next[row.id] = {
              state: 'error',
              message
            }
          })
          return next
        })
      }
    }

    setImportingRows(false)

    if (successCount > 0) {
      addToast({
        variant: 'success',
        title: 'Import complete',
        description:
          `Synced ${successCount} row${successCount === 1 ? '' : 's'} across ` +
          `${successfulOperatorCount} operator${successfulOperatorCount === 1 ? '' : 's'}.`
      })
      await loadOperators()
    }

    if (failureCount > 0) {
      addToast({
        variant: 'error',
        title: 'Some rows need attention',
        description: `${failureCount} row${failureCount === 1 ? '' : 's'} failed to import.`
      })
    }
  }

  const handleExportJson = async () => {
    setExportingJson(true)
    try {
      const data = await getTourOperators()
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
      link.href = url
      link.download = `tour-operators-${timestamp}.json`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      addToast({
        variant: 'success',
        title: 'Export complete',
        description: 'Exported operators JSON.'
      })
    } catch (error) {
      addToast({
        variant: 'error',
        title: 'Unable to export JSON',
        description: getErrorMessage(error)
      })
    } finally {
      setExportingJson(false)
    }
  }

  const handleJsonFileChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) {
        return
      }
      setImportingJson(true)
      try {
        const text = await file.text()
        const parsed = JSON.parse(text)
        if (!Array.isArray(parsed)) {
          throw new Error('JSON must be an array of operators.')
        }
        await importTourOperators(parsed as TourOperator[])
        addToast({
          variant: 'success',
          title: 'Import complete',
          description: `Imported ${parsed.length} operator${parsed.length === 1 ? '' : 's'}.`
        })
        await loadOperators()
      } catch (error) {
        console.error('Failed to import operators JSON', error)
        addToast({
          variant: 'error',
          title: 'Unable to import JSON',
          description: error instanceof Error ? error.message : 'Ensure the file is a valid export.'
        })
      } finally {
        event.target.value = ''
        setImportingJson(false)
      }
    },
    [addToast, loadOperators]
  )

  const handleExportCsv = async () => {
    setExporting(true)
    try {
      const latestOperators = await getPlatformAdminTourOperators()
      const csvText = buildCsvForOperators(latestOperators)
      const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
      link.href = url
      link.download = `hikestrong-tour-operators-${timestamp}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      setOperators(latestOperators)
      addToast({
        variant: 'success',
        title: 'CSV exported',
        description: 'Tour operators CSV downloaded successfully.'
      })
    } catch (error) {
      console.error('Failed to export CSV', error)
      addToast({
        variant: 'error',
        title: 'Unable to export CSV',
        description: getErrorMessage(error)
      })
    } finally {
      setExporting(false)
    }
  }

  if (!isAdmin) {
    return (
      <EmptyState
        icon={AlertTriangle}
        title="Admins only"
        description="Only Admin and Editor roles can manage tour operators."
        className="rounded-2xl border border-dashed border-amber-200 bg-amber-50/60"
      />
    )
  }

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, index) => (
          <div key={index} className="h-24 animate-pulse rounded-2xl border border-border/50 bg-muted/30" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <Card className="border border-rose-200 bg-rose-50/70">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-rose-700">
            <AlertTriangle className="h-5 w-5" />
            Unable to load operators
          </CardTitle>
          <CardDescription className="text-rose-600">{error}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => loadOperators()} variant="accent">
            Retry
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-5">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Operators</p>
          <h1 className="text-3xl font-semibold text-foreground">Tour operator directory</h1>
          <p className="text-sm text-muted-foreground">Create new operators and update existing ones for dashboard access.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={jsonFileInputRef} type="file" accept="application/json" className="hidden" onChange={handleJsonFileChange} />
          <Button
            variant="destructive"
            onClick={() => void handleDeleteAllOperators()}
            disabled={deletingAll || loading || operators.length === 0 || importingJson || exportingJson || importingRows || creating}
          >
            {deletingAll ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting…
              </>
            ) : (
              <>
                <Trash2 className="mr-2 h-4 w-4" />
                Delete All
              </>
            )}
          </Button>
          <Button variant="outline" onClick={handleExportJson} disabled={exportingJson}>
            {exportingJson ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Exporting…
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Export JSON
              </>
            )}
          </Button>
          <Button variant="outline" onClick={() => jsonFileInputRef.current?.click()} disabled={importingJson}>
            {importingJson ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Importing…
              </>
            ) : (
              <>
                <FileJson className="mr-2 h-4 w-4" />
                Import JSON
              </>
            )}
          </Button>
        </div>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Create a new operator</CardTitle>
          <CardDescription>Set up a new tour company for dashboard access.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-4 md:flex-row">
            <Input
              placeholder="Operator name"
              value={newName}
              onChange={(event) => {
                const name = event.target.value
                setNewName(name)
                setNewShortId(generateShortId(name))
              }}
              className="md:flex-1"
            />
            <Input
              placeholder="Short ID"
              value={newShortId}
              onChange={(event) => setNewShortId(event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))}
              className="md:w-28"
              maxLength={8}
            />
            <Input
              placeholder="Referral code (optional)"
              value={newReferralCode}
              onChange={(event) => setNewReferralCode(event.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))}
              className="md:w-48"
            />
            <div className="flex flex-col gap-1 md:w-36">
              <label htmlFor="new-operator-sort-priority" className="text-xs font-medium text-muted-foreground">
                Sort priority
              </label>
              <Input
                id="new-operator-sort-priority"
                type="number"
                min={0}
                step={1}
                value={newSortPriority}
                onChange={(event) => setNewSortPriority(event.target.value)}
              />
            </div>
            <Button className="md:w-auto" onClick={handleCreateOperator} disabled={creating || !newName.trim()}>
              {creating ? 'Creating…' : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Create operator
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Higher sort-priority numbers appear first in the mobile app. This number is only available to platform admins. Short ID is auto-generated from the name (3-8 uppercase alphanumeric chars) and used in R2 storage paths. Referral codes allow tour operators to share a code that links users directly to their hikes.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Existing operators</CardTitle>
          <CardDescription>Manage {sortedOperators.length} operators linked to the platform.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">Logos: PNG or WebP, max 256×256px. Hover over a logo to upload.</p>
          {sortedOperators.length === 0 ? (
            <EmptyState
              icon={Factory}
              title="No operators yet"
              description="Create your first operator to start onboarding tours."
              className="border border-dashed border-border/60 bg-muted/20"
            />
          ) : (
            <div className="space-y-3">
              {sortedOperators.map((operator) => {
                const isEditing = editingId === operator.id
                return (
                  <div
                    key={operator.id}
                    data-testid={`platform-operator-row-${operator.id}`}
                    className="flex flex-col gap-4 rounded-2xl border border-border/60 bg-card/80 p-4 shadow-sm md:flex-row md:items-center md:justify-between"
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div className="relative group">
                        {operator.logoUrl ? (
                          <img
                            src={operator.logoUrl}
                            alt={`${operator.name} logo`}
                            className="w-14 h-14 rounded-lg object-contain border border-border/60"
                          />
                        ) : (
                          <div className="w-14 h-14 rounded-lg bg-muted/50 border border-dashed border-border/60 flex items-center justify-center">
                            <Factory className="h-6 w-6 text-muted-foreground" />
                          </div>
                        )}
                        <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 rounded-lg cursor-pointer transition-opacity">
                          {uploadingLogoId === operator.id ? (
                            <Loader2 className="h-5 w-5 text-white animate-spin" />
                          ) : (
                            <Upload className="h-5 w-5 text-white" />
                          )}
                          <input
                            type="file"
                            accept="image/png,image/webp"
                            className="hidden"
                            disabled={uploadingLogoId === operator.id}
                            onChange={(event) => {
                              const file = event.target.files?.[0]
                              if (file) {
                                handleLogoUpload(operator, file)
                              }
                              event.target.value = ''
                            }}
                          />
                        </label>
                      </div>
                      <div className="flex-1">
                        {isEditing ? (
                          <div className="flex flex-col gap-2 sm:flex-row">
                            <Input
                              value={editName}
                              onChange={(event) => setEditName(event.target.value)}
                              placeholder="Operator name"
                              className="sm:flex-1"
                            />
                            <Input
                              value={editShortId}
                              onChange={(event) => setEditShortId(event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))}
                              placeholder="Short ID"
                              className="sm:w-24"
                              maxLength={8}
                            />
                            <Input
                              value={editReferralCode}
                              onChange={(event) => setEditReferralCode(event.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))}
                              placeholder="Referral code"
                              className="sm:w-36"
                            />
                            <Input
                              aria-label={`Sort priority for ${operator.name}`}
                              type="number"
                              min={0}
                              step={1}
                              value={editSortPriority}
                              onChange={(event) => setEditSortPriority(event.target.value)}
                              className="sm:w-28"
                            />
                          </div>
                        ) : (
                          <>
                            <p className="text-lg font-semibold text-foreground">
                              {operator.name}
                              {operator.shortId && (
                                <span className="ml-2 inline-flex px-1.5 py-0.5 text-xs font-mono font-medium bg-slate-100 text-slate-600 rounded align-middle">
                                  {operator.shortId}
                                </span>
                              )}
                            </p>
                            <p className="text-xs uppercase tracking-wide text-muted-foreground">
                              {operator.tours.length} tours linked
                              {operator.referralCode && (
                                <span className="ml-2 px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded font-mono normal-case">
                                  {operator.referralCode}
                                </span>
                              )}
                              <span className="ml-2 rounded bg-sky-100 px-1.5 py-0.5 font-mono normal-case text-sky-700">
                                Priority {operator.sortPriority}
                              </span>
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {isEditing ? (
                        <>
                          <Button size="sm" onClick={handleSaveOperator} disabled={savingId === operator.id || !editName.trim()}>
                            {savingId === operator.id ? 'Saving…' : 'Save'}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingId(null)
                              setEditName('')
                              setEditSortPriority('0')
                            }}
                            disabled={savingId === operator.id}
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => startEditing(operator)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-rose-600"
                        onClick={() => handleDeleteOperator(operator)}
                        disabled={deletingId === operator.id}
                      >
                        {deletingId === operator.id ? 'Deleting…' : (
                          <>
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border border-dashed border-border/60">
        <CardHeader>
          <CardTitle>Import tours from CSV</CardTitle>
          <CardDescription>Upload the source spreadsheet to preview rows, convert imperial values to metric, and replace selected operators&apos; tour lists.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-4 rounded-2xl border border-dashed border-border/70 bg-muted/30 p-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-white/80 p-2 text-muted-foreground shadow-sm">
                <FileSpreadsheet className="h-5 w-5" />
              </div>
              <div className="text-sm text-muted-foreground">
                <p>
                  Choose the CSV export from Google Sheets (for example <code className="text-xs">/Users/chris/Downloads/hikestrong-tour-operators.csv</code>). We match the headers, convert miles and feet to kilometers and meters, and replace existing tours for the selected operators.
                </p>
                {uploadedFileName && (
                  <p className="mt-1 text-xs text-foreground">
                    Loaded <span className="font-medium">{uploadedFileName}</span> &middot; {parsedRows.length} row{parsedRows.length === 1 ? '' : 's'}
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleFileChange} />
              <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={importingRows}>
                <Upload className="mr-2 h-4 w-4" />
                Select CSV
              </Button>
              <Button variant="outline" onClick={handleExportCsv} disabled={exporting}>
                {exporting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Exporting…
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Export CSV
                  </>
                )}
              </Button>
              {parsedRows.length > 0 && (
                <Button variant="ghost" onClick={handleClearImport} disabled={importingRows}>
                  Clear
                </Button>
              )}
            </div>
          </div>

          {parsedRows.length > 0 && (
            <div className="space-y-3 rounded-2xl border border-border/60 bg-card/80 p-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {selectedCount} of {selectableRows.length} row{selectableRows.length === 1 ? '' : 's'} selected across {selectedOperatorCount} operator{selectedOperatorCount === 1 ? '' : 's'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Rows missing a tour operator or trip name stay disabled. Only selected rows will remain for each imported operator.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={toggleSelectAll}
                    disabled={selectableRows.length === 0 || importingRows}
                  >
                    {isAllSelected ? 'Clear selection' : 'Select all'}
                  </Button>
                  <Button size="sm" onClick={handleImportSelected} disabled={selectedCount === 0 || importingRows}>
                    {importingRows ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Replacing…
                      </>
                    ) : (
                      <>Sync {selectedOperatorCount} operator{selectedOperatorCount === 1 ? '' : 's'}</>
                    )}
                  </Button>
                </div>
              </div>

              <div className="overflow-auto rounded-2xl border border-border/50">
                <table className="min-w-full divide-y divide-border/60 text-left text-sm">
                  <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="w-10 px-3 py-2">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-border accent-foreground"
                          checked={isAllSelected && selectableRows.length > 0}
                          onChange={toggleSelectAll}
                          disabled={selectableRows.length === 0 || importingRows}
                        />
                      </th>
                      <th className="w-16 px-3 py-2">Row</th>
                      <th className="px-3 py-2">Operator</th>
                      <th className="px-3 py-2">Trip</th>
                      <th className="px-3 py-2">Action</th>
                      <th className="w-20 px-3 py-2">Days</th>
                      <th className="w-48 px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40 bg-white/80 text-foreground">
                    {previewRows.map((row) => {
                      const selectable = row.errors.length === 0
                      const checked = selectable && selectedRowIds.has(row.id)
                      const actionLabel = formatActionLabel(row.action)
                      return (
                        <tr key={row.id} className={!selectable ? 'bg-rose-50/70' : undefined}>
                          <td className="px-3 py-3">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-border accent-foreground"
                              checked={checked}
                              disabled={!selectable || importingRows}
                              onChange={() => toggleRowSelection(row.id, !selectable || importingRows)}
                            />
                          </td>
                          <td className="px-3 py-3 text-xs text-muted-foreground">#{row.rowNumber}</td>
                          <td className="px-3 py-3">
                            <p className="text-sm font-medium text-foreground">{row.operatorDisplayName || '—'}</p>
                            {row.sourceOperatorId && (
                              <p className="text-xs text-muted-foreground">CSV operator id: {row.sourceOperatorId}</p>
                            )}
                            {row.matchedOperator && row.operatorNameFromCsv && row.operatorNameFromCsv !== row.matchedOperator.name && (
                              <p className="text-xs text-muted-foreground">Matched existing: {row.matchedOperator.name}</p>
                            )}
                            {row.errors.length > 0 && (
                              <p className="mt-1 text-xs text-rose-600">{row.errors.join(' ')}</p>
                            )}
                          </td>
                          <td className="px-3 py-3">
                            <p className="font-medium text-foreground">{row.tripName || '—'}</p>
                            {row.tripId && <p className="text-xs text-muted-foreground">CSV trip id: {row.tripId}</p>}
                            {row.link && (
                              <a
                                className="text-xs text-blue-600 underline-offset-2 hover:underline"
                                href={row.link}
                                target="_blank"
                                rel="noreferrer"
                              >
                                View itinerary
                              </a>
                            )}
                          </td>
                          <td className="px-3 py-3">
                            <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${actionLabel.className}`}>
                              {actionLabel.label}
                            </span>
                            {row.willCreateOperator && (
                              <p className="mt-1 text-xs text-muted-foreground">A new operator will be created.</p>
                            )}
                          </td>
                          <td className="px-3 py-3">{row.itinerary.length > 0 ? row.itinerary.length : '—'}</td>
                          <td className="px-3 py-3">
                            {row.status?.state === 'success' && (
                              <div className="flex items-center gap-2 text-emerald-600">
                                <CheckCircle2 className="h-4 w-4" />
                                <span className="text-xs font-medium">Imported</span>
                              </div>
                            )}
                            {row.status?.state === 'error' && (
                              <div className="flex items-center gap-2 text-rose-600">
                                <XCircle className="h-4 w-4" />
                                <span className="text-xs font-medium">Failed</span>
                              </div>
                            )}
                            {row.status?.state === 'pending' && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span className="text-xs font-medium">Importing…</span>
                              </div>
                            )}
                            {!row.status && <span className="text-xs text-muted-foreground">—</span>}
                            {row.status?.message && (
                              <p className="text-xs text-muted-foreground">{row.status.message}</p>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  )
}
